import { useToast } from '../context/ToastContext';

export interface OneDriveFile {
  name: string;
  url: string;
}

interface OneDriveSDK {
  open: (options: {
    clientId: string;
    action: string;
    multiSelect?: boolean;
    openInNewWindow?: boolean;
    advanced?: { filter?: string; redirectUri?: string };
    success: (files: { value: Array<{ name: string; webUrl: string }> }) => void;
    error: (err: unknown) => void;
    cancel: () => void;
  }) => void;
}

declare global {
  interface Window { OneDrive?: OneDriveSDK; }
}

function loadSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.OneDrive) { resolve(); return; }
    const s = document.createElement('script');
    s.src = 'https://js.live.net/v7.2/OneDrive.js';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load OneDrive picker'));
    document.head.appendChild(s);
  });
}

interface Props {
  onSelect: (file: OneDriveFile) => void;
  disabled?: boolean;
  label?: string;
}

export default function OneDrivePicker({ onSelect, disabled, label = 'Choose from OneDrive' }: Props) {
  const { showToast } = useToast();

  async function open() {
    try {
      await loadSdk();
      window.OneDrive!.open({
        clientId: import.meta.env.VITE_AZURE_CLIENT_ID as string,
        action: 'share',
        multiSelect: false,
        openInNewWindow: true,
        advanced: {
          filter: '.pdf,.docx,.xlsx,.png,.jpg,.jpeg,.msg',
          redirectUri: `${window.location.origin}/redirect.html`,
        },
        success(files) {
          const f = files.value[0];
          if (f) onSelect({ name: f.name, url: f.webUrl });
        },
        error() {
          showToast('OneDrive picker encountered an error.', 'error');
        },
        cancel() {},
      });
    } catch {
      showToast('Could not load the OneDrive picker.', 'error');
    }
  }

  return (
    <button type="button" className="btn btn--secondary btn--sm" onClick={open} disabled={disabled}>
      <i className="fa-brands fa-microsoft" aria-hidden="true" /> {label}
    </button>
  );
}
